import { Statistic, Typography } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import type { WidgetDefinition, WidgetRenderProps } from '@/types';
import { formatCompactNumber, formatCurrency, formatPercent } from '@/utils';

interface KpiConfig {
  value?: number;
  unit?: string;
  delta?: number;
  format?: 'number' | 'currency' | 'percent' | 'compact';
  color?: string;
}

function formatValue(value: number, format: KpiConfig['format'], unit?: string) {
  switch (format) {
    case 'currency':
      return formatCurrency(value);
    case 'percent':
      return formatPercent(value < 1 ? value : value / 100);
    case 'compact':
      return formatCompactNumber(value);
    default:
      return `${unit ?? ''}${value.toLocaleString()}`;
  }
}

function KpiWidget({ instance }: WidgetRenderProps<KpiConfig>) {
  const { value = 0, delta, format = 'number', unit, color } = instance.config;
  const positive = (delta ?? 0) >= 0;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}
    >
      <Statistic
        value={formatValue(value, format, unit)}
        valueStyle={{ color: color ?? undefined, fontSize: 28 }}
      />
      {delta !== undefined && (
        <Typography.Text type={positive ? 'success' : 'danger'} style={{ marginTop: 8 }}>
          {positive ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(delta).toFixed(1)}
          {format === 'percent' ? ' pts' : '%'}
        </Typography.Text>
      )}
    </div>
  );
}

export const kpiWidgetDef: WidgetDefinition<KpiConfig> = {
  type: 'kpi',
  name: 'KPI',
  description: 'Single metric with trend indicator',
  icon: 'DashboardOutlined',
  category: 'metric',
  defaultTitle: 'KPI',
  defaultConfig: { value: 0, format: 'number' },
  minW: 2,
  minH: 2,
  defaultW: 3,
  defaultH: 3,
  component: KpiWidget,
  settingsSchema: {
    fields: [
      { key: 'value', label: 'Value', type: 'number' },
      { key: 'unit', label: 'Unit', type: 'text' },
      { key: 'delta', label: 'Delta (%)', type: 'number' },
      {
        key: 'format',
        label: 'Format',
        type: 'select',
        options: [
          { label: 'Number', value: 'number' },
          { label: 'Compact', value: 'compact' },
          { label: 'Currency', value: 'currency' },
          { label: 'Percent', value: 'percent' },
        ],
      },
    ],
  },
};
